import React from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Typography,
    Link,
    Paper,
    Button,
} from "@mui/material";
import {
    PlayCircleIcon,
    IdeaIcon,
    EditGridIcon,
    PdfFileIcon,
    CheckCircleIcon,
    GiftBoxIcon,
} from "assets/images/icons/Components";

export const getUSSlides = (t) => [  
    {
        title: t("labels.explore_funding"),
        description: t("messages.jumpstart_journey"),
        linkText: t("labels.watch_demo_slides"),
        linkIcon: PlayCircleIcon,
        linkUrl: "https://grantadvance.supportbee.io/2439-the-simple-funding-solution/6130-demos-us-platform/20504-onboarding-bootcamp-us",
        hasInfoCard: true,
    },
    {
        title: t("labels.join_live_webinar"),
        description: t("messages.have_questions"),
        linkText: t("labels.register_webinar"),
        linkIcon: EditGridIcon,
        linkUrl: "https://grantadvancesolutions.as.me/get-started-now",
    },
    {
        title: t("labels.roadmap_success_guide"),
        description: t("messages.follow_guide"),
        pdfItems: [
            { text: t("labels.roadmap_success_pdf"), url: "/Roadmap to Success - US.pdf" },
            { text: t("labels.charities_program_form"), url: "/Charities Helping Charities Program Form.pdf" }
        ],
        linkIcon: PdfFileIcon,
    },
    {
        title: t("labels.charities_helping_charities_title"),
        description: t("labels.refer_charity_earn_bonus"),
        items: [
            t("messages.referral_bonus_1"),
            t("messages.referral_bonus_2"),
            t("messages.referral_bonus_3")
        ],
        hasReferralLink: true,
        linkText: t("labels.refer_a_charity"),
        linkIcon: GiftBoxIcon,
    }
];

const MessageContentUS = ({ slideData, SlideIcon, userName, onClickOpenReferral }) => {
    const { t } = useTranslation();
    
    if (!slideData) return null;

    return (
        <>
            <Typography
                sx={{
                    color: '#000',
                    fontFamily: '"Work Sans"',
                    fontSize: '20px',
                    fontWeight: 700,
                    letterSpacing: '-0.4px',
                    textAlign: 'center',
                    mb: 1,
                }}
            >
                {slideData.title}
            </Typography>

            <Typography
                sx={{
                    color: '#757575',
                    textAlign: 'center',
                    fontFamily: 'Poppins',
                    fontSize: '18px',
                    fontWeight: 500,
                    lineHeight: '24.01px',
                    mb: 2,
                }}
            >
                {slideData.description.replace('{userName}', userName || 'User')}
            </Typography>

            {slideData.linkText && !slideData.hasReferralLink && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {SlideIcon && <SlideIcon />}
                    <Link
                        href={slideData.linkUrl}
                        target="_blank"
                        underline="hover"
                        sx={{
                            color: '#0288D1',
                            fontFamily: 'Poppins',
                            fontSize: '18px',
                            fontWeight: 500,
                            lineHeight: '24.01px',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {slideData.linkText}
                    </Link>
                </Box>
            )}

            {slideData.pdfItems && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {slideData.pdfItems.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PdfFileIcon />
                            <Link
                                href={item.url}
                                target="_blank"
                                underline="hover"
                                sx={{
                                    color: '#0288D1',
                                    fontFamily: 'Poppins',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '21px',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                {item.text}
                            </Link>
                        </Box>
                    ))}
                </Box>
            )}

            {slideData.items && typeof slideData.items[0] === 'string' && (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: slideData.hasReferralLink ? 2 : 0 }}>
                        {slideData.items.map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                <CheckCircleIcon />
                                <Typography
                                    sx={{
                                        color: '#757575',
                                        fontFamily: 'Poppins',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        lineHeight: '24px',
                                        flex: 1,
                                    }}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    
                    {slideData.hasReferralLink && slideData.linkText && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                mt: 2,
                            }}
                        >
                            {SlideIcon && <SlideIcon />}
                            <Button
                                onClick={() => {
                                    onClickOpenReferral()
                                }}
                                sx={{
                                    color: '#0288D1',
                                    fontFamily: 'Poppins',
                                    fontSize: '18px',
                                    fontWeight: 500,
                                    lineHeight: '24.01px',
                                    textDecoration: 'none',
                                    padding: '6px 0px',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                {slideData.linkText}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {slideData.hasInfoCard && (
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        width: '100%',
                        p: 1,
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: 2,
                        background: '#FAFAFA',
                        mb: 2,
                    }}
                >
                    <IdeaIcon />
                    <Box>
                        <Typography
                            sx={{
                                color: '#757575',
                                fontFamily: 'Poppins',
                                fontSize: '14px',
                                fontWeight: 700,
                                lineHeight: '24px',
                            }}
                        >
                            {t("labels.optimal_browser_use")}
                        </Typography>
                        <Typography
                            sx={{
                                color: '#424242',
                                fontFamily: 'Poppins',
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: 'normal',
                                letterSpacing: '-0.26px',
                            }}
                        >
                            {t("labels.browser_recommendation")}
                        </Typography>
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default MessageContentUS;